import type { InputBaseComponentProps } from "@mui/material";
import type { KeyboardEvent, ReactNode, Ref, RefObject } from "react";

import { Box, Icon, IconButton } from "@mui/material";
import InputBase from "@mui/material/InputBase";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import ShortcutHintBadge from "~/components/keyboard/ShortcutHintBadge";
import globalSearchFocus from "~/keyboard/globalSearchFocus";

interface GlobalSearchFieldProps {
	anchorRef?: Ref<HTMLDivElement>;
	children?: ReactNode;
	inputProps: InputBaseComponentProps;
	inputRef: RefObject<HTMLInputElement | null>;
	onBlur?: () => void;
	onChange: (value: string) => void;
	onClear: () => void;
	onFocus?: () => void;
	onKeyDown: (event: KeyboardEvent) => void;
	placeholder: string;
	tourId?: string;
	value: string;
	width: string;
}

const GlobalSearchField = ({
	anchorRef,
	children,
	inputProps,
	inputRef,
	onBlur,
	onChange,
	onClear,
	onFocus,
	onKeyDown,
	placeholder,
	tourId,
	value,
	width
}: GlobalSearchFieldProps) => {
	const { t } = useTranslation("common");

	useEffect(
		() =>
			globalSearchFocus.subscribe(() => {
				const input = inputRef.current;
				if (!input) {
					return;
				}
				input.focus();
				input.select();
			}),
		[inputRef]
	);

	return (
		<Box
			data-tour-id={tourId}
			ref={anchorRef}
			sx={{
				"&:focus-within": { backgroundColor: "background.paper", borderColor: "primary.main" },
				"&:hover": { borderColor: "text.disabled" },
				alignItems: "center",
				backgroundColor: "action.hover",
				border: 1,
				borderColor: "divider",
				borderRadius: 100,
				display: "flex",
				gap: 0.75,
				height: 38,
				position: "relative",
				px: 1.5,
				transition: "background-color 0.2s ease, border-color 0.2s ease",
				width
			}}
		>
			<ShortcutHintBadge actionId="focusGlobalSearch" placement="bottom-left" />
			<Icon sx={{ color: "text.disabled", fontSize: "1.05rem" }}>search</Icon>
			<InputBase
				inputProps={inputProps}
				inputRef={inputRef}
				onBlur={onBlur}
				onChange={(event) => onChange(event.target.value)}
				onFocus={onFocus}
				onKeyDown={onKeyDown}
				placeholder={placeholder}
				sx={{ flex: 1, fontSize: "0.8125rem", lineHeight: 1 }}
				value={value}
			/>
			{value && (
				<IconButton aria-label={t("search.clearAria")} onClick={onClear} size="small" sx={{ p: 0.25 }}>
					<Icon sx={{ fontSize: "0.95rem" }}>clear</Icon>
				</IconButton>
			)}
			{children}
		</Box>
	);
};

export default GlobalSearchField;
